import type { FC, FormEvent, ReactNode } from 'react'

import { useState } from 'react'
import { CheckCircle2, Loader2 } from 'lucide-react'

const TOPICS = ['DOCSCRUB', 'Premium or billing', 'Free image tools', 'Morpho Radar', 'Something else']

type Status = 'idle' | 'sending' | 'sent' | 'error'

const fieldClass = 'w-full rounded-xl border border-white/10 bg-[#0a1424] px-4 py-3 text-sm text-white placeholder:text-slate-500 focus:border-cyan-300/60 focus:outline-none focus:ring-2 focus:ring-cyan-300/20'

const ContactForm: FC = (): ReactNode => {
    const [status, setStatus] = useState<Status>('idle')
    const [error, setError] = useState('')

    const handleSubmit = async (event: FormEvent<HTMLFormElement>) => {
        event.preventDefault()
        const form = event.currentTarget
        const data = Object.fromEntries(new FormData(form).entries())

        setStatus('sending')
        setError('')

        try {
            const response = await fetch('/api/contact', {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify(data)
            })
            const result = await response.json().catch(() => ({}))

            if (!response.ok) {
                setStatus('error')
                setError(result.message ?? 'Something went wrong. Please try again.')
                return
            }

            form.reset()
            setStatus('sent')
        }
        catch {
            setStatus('error')
            setError('We could not reach the server. Please check your connection and try again.')
        }
    }

    if (status === 'sent') {
        return (
            <div className='rounded-2xl border border-cyan-300/30 bg-cyan-300/5 p-8 text-center' role='status'>
                <CheckCircle2 className='mx-auto size-6 text-cyan-300' />
                <h3 className='mt-4 text-lg font-bold text-white'>Message sent</h3>
                <p className='mt-2 text-sm text-slate-400'>Thanks for getting in touch. We will reply to the email address you gave us.</p>
                <button type='button' onClick={() => setStatus('idle')} className='mt-6 text-sm font-semibold text-cyan-300 hover:underline'>
                    Send another message
                </button>
            </div>
        )
    }

    return (
        <form onSubmit={handleSubmit} className='space-y-5' noValidate={false}>
            <div className='grid gap-5 sm:grid-cols-2'>
                <div>
                    <label htmlFor='contact-name' className='mb-2 block text-sm font-medium text-slate-300'>Your name</label>
                    <input id='contact-name' name='name' required maxLength={120} autoComplete='name' className={fieldClass} />
                </div>
                <div>
                    <label htmlFor='contact-email' className='mb-2 block text-sm font-medium text-slate-300'>Email</label>
                    <input id='contact-email' name='email' type='email' required maxLength={200} autoComplete='email' className={fieldClass} />
                </div>
            </div>

            <div>
                <label htmlFor='contact-topic' className='mb-2 block text-sm font-medium text-slate-300'>What is it about?</label>
                <select id='contact-topic' name='topic' className={fieldClass} defaultValue='DOCSCRUB'>
                    {TOPICS.map((topic) => <option key={topic} value={topic}>{topic}</option>)}
                </select>
            </div>

            <div>
                <label htmlFor='contact-message' className='mb-2 block text-sm font-medium text-slate-300'>Message</label>
                <textarea
                    id='contact-message'
                    name='message'
                    required
                    minLength={10}
                    maxLength={5000}
                    rows={6}
                    placeholder='Tell us your device, app version, the tool you used and what happened. Please do not include passwords, payment details or private documents.'
                    className={fieldClass}
                />
            </div>

            <div className='hidden' aria-hidden='true'>
                <label htmlFor='contact-website'>Website</label>
                <input id='contact-website' name='website' tabIndex={-1} autoComplete='off' />
            </div>

            {status === 'error' && <p className='text-sm text-rose-300' role='alert'>{error}</p>}

            <button
                type='submit'
                disabled={status === 'sending'}
                className='inline-flex w-full items-center justify-center gap-2 rounded-xl bg-cyan-300 px-6 py-3.5 text-sm font-bold text-[#06121f] transition hover:bg-cyan-200 disabled:opacity-60 sm:w-auto'
            >
                {status === 'sending' && <Loader2 className='size-4 animate-spin' />}
                {status === 'sending' ? 'Sending' : 'Send message'}
            </button>
        </form>
    )
}

export default ContactForm
